import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ProfilePage from "@/pages/ProfilePage";
import ChatPage from "@/pages/ChatPage";
import AttendancePage from "@/pages/AttendancePage";
import PhotosPage from "@/pages/PhotosPage";
import MaterialsPage from "@/pages/MaterialsPage";
import ProjectsPage from "@/pages/ProjectsPage";
import StatsPage from "@/pages/StatsPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TeamAttendancePage from "@/pages/TeamAttendancePage";
import EquipmentPage from "@/pages/EquipmentPage";
import ToolsPage from "@/pages/ToolsPage";
import MaintenancePage from "@/pages/MaintenancePage";
import MissionsPage from "@/pages/MissionsPage";
import ReceptionPage from "@/pages/ReceptionPage";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import OfflineBanner from "@/components/OfflineBanner";

// Layout
import MainLayout from "@/components/layout/MainLayout";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <MainLayout><ErrorBoundary>{children}</ErrorBoundary></MainLayout>;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="spinner" />
      </div>
    );
  }
  
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.role === 'subcontractor' ? '/missions' : '/'} replace /> : <LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<ProtectedRoute>{user?.role === 'subcontractor' ? <Navigate to="/missions" replace /> : <DashboardPage />}</ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/chat/:userId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
      <Route path="/team-attendance" element={<ProtectedRoute><TeamAttendancePage /></ProtectedRoute>} />
      <Route path="/photos" element={<ProtectedRoute><PhotosPage /></ProtectedRoute>} />
      <Route path="/materials" element={<ProtectedRoute><MaterialsPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      <Route path="/equipment" element={<ProtectedRoute><EquipmentPage /></ProtectedRoute>} />
      <Route path="/tools" element={<ProtectedRoute><ToolsPage /></ProtectedRoute>} />
      <Route path="/maintenance" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
      <Route path="/missions" element={<ProtectedRoute>{(user?.role === 'manager' || user?.role === 'subcontractor') ? <MissionsPage /> : <Navigate to="/" replace />}</ProtectedRoute>} />
      <Route path="/reception" element={<ProtectedRoute><ReceptionPage /></ProtectedRoute>} />
      <Route path="/reception/:projectId" element={<ProtectedRoute><ReceptionPage /></ProtectedRoute>} />
      <Route path="/stats" element={<ProtectedRoute><StatsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="texture-overlay" />
          <OfflineBanner />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
